import React from 'react';
// @ts-expect-error
import {Controller} from 'react-hook-form';
// @ts-expect-error
import type {Control, Watch} from 'react-hook-form';

import {Button} from 'shells/button/button';
import {Checkbox} from 'shells/checkbox/checkbox';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {SelectPickerGeneric} from 'shells/select-picker-generic/select-picker-generic';

import {analyticsClient} from '../../../../../analytics-client';

type Props = {
    name: string;
    watch: Watch;
    control: Control;
    fieldType: string;
    answerOptions: AnswerOption[];
};

type AnswerOption = {
    id: string;
    name: string;
};

export function PreselectAnswers(props: Props) {
    const defaultValues = props.watch(`${props.name}.defaultValues`);

    const [isChecked, setIsChecked] = React.useState(defaultValues.length > 0);
    const [isAddingAnswerChoice, setIsAddingAnswerChoice] = React.useState(false);

    // reset state when switching fields
    React.useEffect(() => {
        setIsChecked(defaultValues.length > 0);
        setIsAddingAnswerChoice(false);
    }, [props.name, defaultValues]);

    const canPreselectMoreAnswers =
        defaultValues.length === 0 ||
        (props.fieldType === 'checkboxes' && defaultValues.length < props.answerOptions.length);

    const availablePreselectOptions = props.answerOptions.map((option) => ({
        ...option,
        isDisabled: defaultValues.includes(option.id),
    }));

    return (
        <>
            <Controller
                name={`${props.name}.defaultValues`}
                control={props.control}
                // @ts-expect-error
                render={({field}) => (
                    <Checkbox
                        // @ts-expect-error
                        className='mt-8 mb-8'
                        label='Pre-select an answer to this question'
                        checked={isChecked || defaultValues.length > 0}
                        onChange={() => {
                            if (isChecked) {
                                field.onChange([]);
                                setIsAddingAnswerChoice(false);
                            } else {
                                setIsAddingAnswerChoice(true);
                            }
                            setIsChecked(!isChecked);
                        }}
                    />
                )}
            />
            {isChecked && (
                <Controller
                    name={`${props.name}.defaultValues`}
                    control={props.control}
                    // @ts-expect-error
                    render={({field}) => {
                        return (
                            <StackLegacy spacing={8}>
                                {/* @ts-expect-error */}
                                {defaultValues.map((preselectedOptionId) => (
                                    <SelectPickerGeneric
                                        key={preselectedOptionId}
                                        valueKey='id'
                                        labelKey='name'
                                        placeholder='Select an answer choice...'
                                        options={availablePreselectOptions}
                                        value={availablePreselectOptions.find(
                                            (option) => option.id === preselectedOptionId
                                        )}
                                        // @ts-expect-error
                                        onChange={(option) => {
                                            // @ts-expect-error
                                            const newDefaultValues = defaultValues.map((id) =>
                                                id === preselectedOptionId ? option.id : id
                                            );
                                            field.onChange(newDefaultValues);
                                        }}
                                        onClear={
                                            // Only checkbox fields can have multiple pre-selected answers so allow clearing of dropdowns
                                            props.fieldType === 'checkboxes' &&
                                            (defaultValues.length > 1 || isAddingAnswerChoice)
                                                ? () => {
                                                      const newDefaultValues = defaultValues.filter(
                                                          // @ts-expect-error
                                                          (id) => id !== preselectedOptionId
                                                      );
                                                      field.onChange(newDefaultValues);
                                                  }
                                                : undefined
                                        }
                                    />
                                ))}
                                {canPreselectMoreAnswers && (
                                    <>
                                        {!isAddingAnswerChoice && defaultValues.length > 0 ? (
                                            <Button
                                                variant='text-primary'
                                                onClick={() => setIsAddingAnswerChoice(true)}
                                            >
                                                + Pre-select another answer
                                            </Button>
                                        ) : (
                                            <SelectPickerGeneric
                                                valueKey='id'
                                                labelKey='name'
                                                placeholder='Select an answer choice...'
                                                value={null}
                                                options={availablePreselectOptions}
                                                // @ts-expect-error
                                                onChange={(option) => {
                                                    analyticsClient.track(
                                                        analyticsClient.EVENTS
                                                            .TRACK_PRESELECTED_FORM_FIELD_CONFIGURED
                                                    );
                                                    setIsAddingAnswerChoice(false);
                                                    field.onChange([...defaultValues, option.id]);
                                                }}
                                            />
                                        )}
                                    </>
                                )}
                            </StackLegacy>
                        );
                    }}
                />
            )}
        </>
    );
}
